import { GenericTable, Spinner } from "@visiblethread/react-components";
import Flex, { GAP_CLASS } from "components/Flex";
import { VIRTUALIZE_TABLE } from "constants/app";
import { arrayOf, bool, number, shape, string } from "prop-types";
import { useEffect, useMemo, useRef } from "react";
import { toTitleCase } from "utils/string";

import RequirementChip from "../RequirementChip";
import classes from "./RequirementsFeed.module.scss";

const RequirementsFeed = ({ items = [], total = 0, isRunning = false }) => {
  const tableRef = useRef(null);
  const rows = useMemo(
    () => items.map((item, index) => ({ ...item, id: index })),
    [items]
  );

  useEffect(() => {
    if (rows.length === 0) {
      return;
    }

    tableRef.current?.scrollToRow(rows.length - 1, {
      align: "end",
      behavior: "auto",
    });
  }, [rows.length]);

  return (
    <div className={classes["feed"]}>
      <div className={classes["feed__header"]}>
        Requirements Found{" "}
        <span className={classes["feed__count"]}>{total}</span>
      </div>
      <div className={classes["feed__table"]}>
        <GenericTable
          headerCellClassName={classes.headerCell}
          columns={[
            {
              accessorKey: "text",
              enableSorting: false,
              header: (
                <Flex className={classes.flex} justifyContent="space-between">
                  <div>Requirement</div>
                  <div>Type</div>
                </Flex>
              ),
              id: "text",
              meta: {
                flexGrow: 1,
              },
              cell: ({ getValue, row }) => {
                const { category } = row.original;
                const categories = (category || "").split("|");
                const parsedCategories = categories.filter(Boolean);

                return (
                  <Flex className={classes.flex} justifyContent="space-between">
                    <div>{getValue()}</div>
                    <div>
                      {!parsedCategories.length
                        ? "—"
                        : parsedCategories.map((cat) => (
                            <RequirementChip
                              key={cat}
                              value={toTitleCase(cat)}
                            />
                          ))}
                    </div>
                  </Flex>
                );
              },
            },
          ]}
          data={rows}
          defaultSortColumnIndex={-1}
          emptyMessage="No requirements found yet."
          isVirtualized={VIRTUALIZE_TABLE}
          ref={tableRef}
          rowIdKey="id"
        />
        {isRunning && rows?.length > 0 && (
          <Flex
            justifyContent="center"
            gap={GAP_CLASS.GAP_150}
            className={classes["feed__footer"]}
            role="status"
          >
            <span className={classes["feed__spinner"]} aria-hidden="true">
              <Spinner opacity={1} size={14} />
            </span>
            <span>Finding More Requirements...</span>
          </Flex>
        )}
      </div>
    </div>
  );
};

RequirementsFeed.propTypes = {
  isRunning: bool,
  items: arrayOf(shape({ category: string, text: string })),
  total: number,
};

export default RequirementsFeed;
